import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import type { ExportFormatStructureNode } from "@capture/shared";

interface Props {
  formatType: string;
  config: Record<string, unknown>;
  columns: Array<{ name: string; order: number; position?: number; length?: number; dataType?: string }>;
  structure: ExportFormatStructureNode[];
  /** Partner Profile's stored sample/preview blob (Task 6) — feeds matching
   * columns instead of synthesized placeholders. Optional; best-effort. */
  sampleData?: unknown;
}

/**
 * E-023 — live LAYOUT preview for the Export Format editor. Renders the current
 * (possibly unsaved) format through `POST /export-formats/preview`, which runs the
 * SAME generators the worker uses (worker-faithful — unlike the form-config
 * client-side preview-renderer). Shows the SHAPE with sample placeholder values;
 * real field data is per-destination and not known at the format level.
 */
export function FormatLayoutPreview({ formatType, config, columns, structure, sampleData }: Props) {
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Re-run on any edit; serialize the inputs so the effect fires on deep changes.
  const payload = JSON.stringify({ formatType, config, columns, structure, sampleData });

  useEffect(() => {
    let cancelled = false;
    // Debounce so rapid typing doesn't spam the endpoint.
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.post<{ preview: string }>("/export-formats/preview", {
          formatType,
          config,
          columns,
          structure,
          sampleData,
        });
        if (!cancelled) setPreview(data.preview);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Preview generation failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  return (
    <section className="rb-card" style={{ marginBottom: "var(--space-4)" }}>
      <h2 className="rb-section-title">Layout Preview</h2>
      <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)", marginBottom: "var(--space-2)" }}>
        Rendered through the same export engine the worker uses, with sample placeholder values. Shows the output
        shape — real field data is mapped per Job Type.
      </p>
      {error && (
        <div role="alert" style={{ color: "var(--color-error)", fontSize: "var(--font-size-sm)" }}>
          {error}
        </div>
      )}
      {loading && preview == null && <div style={{ color: "var(--color-text-muted)" }}>Generating preview…</div>}
      {preview != null && (
        <pre
          aria-label="Export layout preview"
          style={{
            background: "var(--color-surface-sunken, #1e1e1e)",
            color: "var(--color-text-primary, #e0e0e0)",
            padding: "var(--space-3)",
            borderRadius: "var(--radius-md)",
            overflowX: "auto",
            fontSize: "var(--font-size-sm)",
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          <code>{preview}</code>
        </pre>
      )}
    </section>
  );
}
